(() => {
  const selectors = {
    drawer: '[data-cart-drawer-shell]',
    drawerBody: '[data-cart-drawer-body]',
    drawerSubtotal: '[data-cart-drawer-subtotal]',
    drawerPromo: '[data-cart-drawer-promo]',
    cartCount: '[data-cart-count]',
    promotionZones: '[data-active-promotions]'
  };

  const storageKey = 'cisqo_discounts';
  const legacyStorageKey = 'cisqo_discount';

  const money = (cents) => {
    try {
      return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format((Number(cents) || 0) / 100);
    } catch (error) {
      return `${((Number(cents) || 0) / 100).toFixed(2).replace('.', ',')} €`;
    }
  };

  const escapeHTML = (value = '') => String(value).replace(/[&<>'"]/g, character => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  })[character]);

  const routeJSON = (route) => route.endsWith('.js') ? route : `${route}.js`;
  const uniqueCodes = (codes = []) => [...new Set(codes.map(code => String(code || '').trim()).filter(Boolean))];

  const readStoredCodes = () => {
    let codes = [];
    try {
      const stored = JSON.parse(localStorage.getItem(storageKey) || '[]');
      if (Array.isArray(stored)) codes = stored;
    } catch (error) {
      console.warn('Lecture des offres CISQO impossible.', error);
    }
    try {
      const legacy = localStorage.getItem(legacyStorageKey);
      if (legacy) codes.push(legacy);
    } catch (error) {
      console.warn('Lecture de l’ancien code CISQO impossible.', error);
    }
    if (window.CISQO.welcomeCode) codes.unshift(window.CISQO.welcomeCode);
    return uniqueCodes(codes);
  };

  const saveCodes = (codes) => {
    const normalized = uniqueCodes(codes);
    try {
      localStorage.setItem(storageKey, JSON.stringify(normalized));
      if (normalized[0]) localStorage.setItem(legacyStorageKey, normalized[0]);
    } catch (error) {
      console.warn('Enregistrement des offres CISQO impossible.', error);
    }
    renderPromotionZones(normalized);
    return normalized;
  };

  const activateCodes = (...codes) => saveCodes([...readStoredCodes(), ...codes]);

  const codesFromCart = (cart) => {
    const attributeCodes = String(cart?.attributes?.cisqo_active_discounts || '').split('|');
    return uniqueCodes([...readStoredCodes(), ...attributeCodes]);
  };

  const checkoutURL = () => {
    const codes = readStoredCodes();
    if (!codes.length) return window.CISQO.routes.checkout || '/checkout';
    if (codes.length === 1) {
      return `/discount/${encodeURIComponent(codes[0])}?redirect=${encodeURIComponent(window.CISQO.routes.checkout || '/checkout')}`;
    }
    return `${window.CISQO.routes.checkout || '/checkout'}?discount=${encodeURIComponent(codes.join(','))}`;
  };

  const renderPromotionZones = (codes, appliedDiscounts = []) => {
    const normalized = uniqueCodes(codes);
    const applied = Array.isArray(appliedDiscounts) ? appliedDiscounts : [];
    const tags = normalized.map(code => `<span class="promotion-tag">✓ Offre activée : ${escapeHTML(code)}</span>`).join('');
    const appliedTags = applied.map(discount => `<span class="promotion-tag promotion-tag--applied">Réduction Shopify : ${escapeHTML(discount.title || '')} −${money(discount.total_allocated_amount || 0)}</span>`).join('');
    const note = normalized.length ? '<small>Les réductions sont vérifiées au paiement selon les règles de cumul définies dans Shopify.</small>' : '';
    document.querySelectorAll(selectors.promotionZones).forEach(zone => {
      zone.innerHTML = `${tags}${appliedTags}${note}`;
      zone.hidden = !normalized.length && !applied.length;
    });
  };

  const updateCount = (count) => {
    document.querySelectorAll(selectors.cartCount).forEach(node => {
      node.textContent = count;
      node.hidden = Number(count) < 1;
    });
  };

  const getDrawer = () => document.querySelector(selectors.drawer);

  const closeDrawer = () => {
    const drawer = getDrawer();
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('drawer-open');
  };

  const renderCart = (cart) => {
    updateCount(cart.item_count);
    const body = document.querySelector(selectors.drawerBody);
    const subtotal = document.querySelector(selectors.drawerSubtotal);
    const codes = codesFromCart(cart);
    saveCodes(codes);
    renderPromotionZones(codes, cart.cart_level_discount_applications || []);

    if (!body || !subtotal) return;
    subtotal.textContent = money(cart.total_price);

    if (!cart.items.length) {
      body.innerHTML = `<div class="drawer-empty"><svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M3 3h2l2.2 10.2a2 2 0 0 0 2 1.6h7.9a2 2 0 0 0 2-1.6L21 7H6"/><circle cx="10" cy="20" r="1"/><circle cx="18" cy="20" r="1"/></svg><p>${window.CISQO.strings.empty}</p><a class="button button--accent" href="/collections/all">Découvrir les produits</a></div>`;
      return;
    }

    body.innerHTML = cart.items.map(item => {
      const bundleCode = item.properties && item.properties['_CISQO Pack'] ? `<span class="cart-pack-label">Pack CISQO · ${escapeHTML(item.properties['_CISQO Pack'])}</span>` : '';
      const saving = item.original_line_price > item.final_line_price ? `<span class="discount-saving">Économie ${money(item.original_line_price - item.final_line_price)}</span>` : '';
      return `
        <article class="drawer-line" data-drawer-line="${escapeHTML(item.key)}">
          ${item.image ? `<img src="${item.image}" alt="${escapeHTML(item.product_title)}" width="164" height="164">` : '<div></div>'}
          <div class="drawer-line__content">
            <h3><a href="${item.url}">${escapeHTML(item.product_title)}</a></h3>
            ${item.variant_title && item.variant_title !== 'Default Title' ? `<p>Variante : ${escapeHTML(item.variant_title)}</p>` : ''}
            ${bundleCode}
            <strong>${money(item.final_line_price)}</strong>${saving}
            <div class="drawer-quantity" aria-label="Quantité de ${escapeHTML(item.product_title)}">
              <button type="button" data-cart-qty-action="decrease" data-line-key="${escapeHTML(item.key)}" aria-label="Diminuer">−</button>
              <input type="number" min="0" value="${item.quantity}" data-cart-quantity data-line-key="${escapeHTML(item.key)}" aria-label="Quantité">
              <button type="button" data-cart-qty-action="increase" data-line-key="${escapeHTML(item.key)}" aria-label="Augmenter">+</button>
            </div>
          </div>
          <button class="drawer-remove" type="button" data-cart-remove="${escapeHTML(item.key)}" aria-label="Retirer ${escapeHTML(item.product_title)}">×</button>
        </article>`;
    }).join('');
  };

  const fetchCart = async () => {
    const response = await fetch(routeJSON(window.CISQO.routes.cart), { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Impossible de charger le panier.');
    return response.json();
  };

  const updateCartAttributes = async (codes, currentCart = null) => {
    const normalized = uniqueCodes(codes);
    const currentValue = String(currentCart?.attributes?.cisqo_active_discounts || '');
    const nextValue = normalized.join('|');
    if (currentCart && currentValue === nextValue) return currentCart;
    const response = await fetch(routeJSON(window.CISQO.routes.cartUpdate), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { cisqo_active_discounts: nextValue } })
    });
    if (!response.ok) throw new Error('Impossible de synchroniser les offres.');
    return response.json();
  };

  const refreshCart = async () => {
    try {
      let cart = await fetchCart();
      const codes = codesFromCart(cart);
      cart = await updateCartAttributes(codes, cart);
      renderCart(cart);
      return cart;
    } catch (error) {
      console.error(error);
      renderPromotionZones(readStoredCodes());
      return null;
    }
  };

  const openDrawer = async () => {
    await refreshCart();
    const drawer = getDrawer();
    if (!drawer) return;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('drawer-open');
    drawer.querySelector('[data-cart-drawer-close]')?.focus();
  };

  const addItems = async (items) => {
    const response = await fetch(routeJSON(window.CISQO.routes.cartAdd), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items })
    });
    const payload = await response.json();
    if (!response.ok) throw new Error(payload.description || window.CISQO.strings.error);
    return payload;
  };

  const changeLine = async (key, quantity) => {
    const response = await fetch(routeJSON(window.CISQO.routes.cartChange), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: Math.max(0, Number(quantity) || 0) })
    });
    const cart = await response.json();
    if (!response.ok) throw new Error(cart.description || window.CISQO.strings.error);
    renderCart(cart);
    return cart;
  };

  const selectedBundleVariant = (item) => {
    const control = item.querySelector('[data-bundle-variant]');
    if (!control) return null;
    const option = control.tagName === 'SELECT' ? control.selectedOptions[0] : control;
    return {
      id: Number(control.value),
      price: Number(option?.dataset.price || control.dataset.price || 0)
    };
  };

  const updateBundleSection = (section) => {
    if (!section) return;
    let total = 0;
    section.querySelectorAll('[data-bundle-item]').forEach(item => {
      const variant = selectedBundleVariant(item);
      const quantityInput = item.querySelector('[data-bundle-quantity]');
      const quantity = Math.max(1, Number(quantityInput?.value || 1));
      if (quantityInput) quantityInput.value = quantity;
      const lineTotal = (variant?.price || 0) * quantity;
      total += lineTotal;
      const lineTotalNode = item.querySelector('[data-bundle-line-total]');
      if (lineTotalNode) lineTotalNode.textContent = money(lineTotal);
    });
    const totalNode = section.querySelector('[data-bundle-total]');
    if (totalNode) totalNode.textContent = money(total);
  };

  const updateProductTotal = (root) => {
    if (!root) return;
    const quantity = Math.max(1, Number(root.querySelector('[data-product-quantity]')?.value || 1));
    const quantityInput = root.querySelector('[data-product-quantity]');
    if (quantityInput) quantityInput.value = quantity;
    const price = Number(root.dataset.currentPrice || 0);
    const totalNode = root.querySelector('[data-product-line-total]');
    if (totalNode) totalNode.textContent = money(price * quantity);
  };

  const updateProductVariant = (selector) => {
    const productRoot = selector.closest('[data-product-root]');
    const variantsNode = productRoot?.querySelector('[data-product-variants]');
    if (!variantsNode) return;
    let variants;
    try { variants = JSON.parse(variantsNode.textContent); } catch (error) { return; }
    const selected = [...productRoot.querySelectorAll('[data-option-selector]')].map(select => select.value);
    const variant = variants.find(item => item.options.every((option, index) => option === selected[index]));
    if (!variant) return;

    const idInput = productRoot.querySelector('input[name="id"]');
    if (idInput) idInput.value = variant.id;
    productRoot.dataset.currentPrice = variant.price;

    const price = productRoot.querySelector('[data-product-price]');
    if (price) price.textContent = money(variant.price);
    const compare = productRoot.querySelector('[data-product-compare-price]');
    if (compare) {
      compare.textContent = variant.compare_at_price > variant.price ? money(variant.compare_at_price) : '';
      compare.hidden = !(variant.compare_at_price > variant.price);
    }
    const button = productRoot.querySelector('[data-product-submit]');
    if (button) {
      button.disabled = !variant.available;
      button.textContent = variant.available ? 'Ajouter au panier' : 'Épuisé';
    }
    const buyNow = productRoot.querySelector('[data-buy-now]');
    if (buyNow) buyNow.disabled = !variant.available;

    if (variant.featured_image?.src) {
      const mainImage = productRoot.querySelector('[data-product-main-image]');
      if (mainImage) {
        mainImage.src = variant.featured_image.src;
        mainImage.srcset = '';
      }
    }

    updateProductTotal(productRoot);
    if (history.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url.toString());
    }
  };

  document.addEventListener('click', async event => {
    const open = event.target.closest('[data-cart-drawer-open]');
    if (open) {
      event.preventDefault();
      await openDrawer();
      return;
    }

    if (event.target.closest('[data-cart-drawer-close]') || event.target.matches('[data-cart-overlay]')) {
      closeDrawer();
      return;
    }

    const discountLink = event.target.closest('[data-discount-code]');
    if (discountLink?.dataset.discountCode) activateCodes(discountLink.dataset.discountCode);

    const checkout = event.target.closest('[data-checkout-link]');
    if (checkout) {
      event.preventDefault();
      await refreshCart();
      window.location.href = checkoutURL();
      return;
    }

    const remove = event.target.closest('[data-cart-remove]');
    if (remove) {
      remove.disabled = true;
      try { await changeLine(remove.dataset.cartRemove, 0); } catch (error) { console.error(error); }
      return;
    }

    const drawerQuantityButton = event.target.closest('[data-cart-qty-action]');
    if (drawerQuantityButton) {
      const input = document.querySelector(`[data-cart-quantity][data-line-key="${CSS.escape(drawerQuantityButton.dataset.lineKey)}"]`);
      if (!input) return;
      const next = Number(input.value || 0) + (drawerQuantityButton.dataset.cartQtyAction === 'increase' ? 1 : -1);
      drawerQuantityButton.disabled = true;
      try { await changeLine(drawerQuantityButton.dataset.lineKey, next); } catch (error) { console.error(error); }
      return;
    }

    const cartPageQuantityButton = event.target.closest('[data-cart-page-qty-action]');
    if (cartPageQuantityButton) {
      const input = document.querySelector(`[data-cart-page-quantity][data-line-key="${CSS.escape(cartPageQuantityButton.dataset.lineKey)}"]`);
      if (!input) return;
      const next = Math.max(0, Number(input.value || 0) + (cartPageQuantityButton.dataset.cartPageQtyAction === 'increase' ? 1 : -1));
      cartPageQuantityButton.disabled = true;
      try {
        await changeLine(cartPageQuantityButton.dataset.lineKey, next);
        window.location.reload();
      } catch (error) {
        console.error(error);
        cartPageQuantityButton.disabled = false;
      }
      return;
    }

    const productQuantityButton = event.target.closest('[data-product-qty-action]');
    if (productQuantityButton) {
      const root = productQuantityButton.closest('[data-product-root]');
      const input = root?.querySelector('[data-product-quantity]');
      if (!input) return;
      input.value = Math.max(1, Number(input.value || 1) + (productQuantityButton.dataset.productQtyAction === 'increase' ? 1 : -1));
      updateProductTotal(root);
      return;
    }

    const bundleQuantityButton = event.target.closest('[data-bundle-qty-action]');
    if (bundleQuantityButton) {
      const section = bundleQuantityButton.closest('[data-bundle-section]');
      const input = bundleQuantityButton.closest('[data-bundle-item]')?.querySelector('[data-bundle-quantity]');
      if (!input) return;
      input.value = Math.max(1, Number(input.value || 1) + (bundleQuantityButton.dataset.bundleQtyAction === 'increase' ? 1 : -1));
      updateBundleSection(section);
      return;
    }

    const buyNow = event.target.closest('[data-buy-now]');
    if (buyNow) {
      const form = buyNow.closest('form');
      if (!form) return;
      buyNow.disabled = true;
      try {
        const formData = new FormData(form);
        await addItems([{ id: Number(formData.get('id')), quantity: Math.max(1, Number(formData.get('quantity') || 1)) }]);
        await refreshCart();
        window.location.href = checkoutURL();
      } catch (error) {
        const message = form.querySelector('[data-form-message]');
        if (message) message.textContent = error.message;
        buyNow.disabled = false;
      }
      return;
    }

    const bundleButton = event.target.closest('[data-bundle-add]');
    if (bundleButton) {
      const section = bundleButton.closest('[data-bundle-section]');
      const message = section?.querySelector('[data-bundle-message]');
      const code = section?.dataset.discountCode;
      const items = [...section.querySelectorAll('[data-bundle-item]')].map(item => {
        const variant = selectedBundleVariant(item);
        const quantity = Math.max(1, Number(item.querySelector('[data-bundle-quantity]')?.value || 1));
        if (!variant?.id) return null;
        return {
          id: variant.id,
          quantity,
          properties: code ? { '_CISQO Pack': code } : { '_CISQO Pack': 'Pack CISQO' }
        };
      }).filter(Boolean);

      if (!items.length) {
        if (message) message.textContent = 'Sélectionnez les produits du pack dans l’éditeur du thème.';
        return;
      }

      bundleButton.disabled = true;
      if (message) message.textContent = 'Ajout automatique du pack…';
      try {
        await addItems(items);
        if (code) activateCodes(code);
        await refreshCart();
        if (message) message.textContent = 'Pack, variantes, quantités et offre ajoutés au panier.';
        await openDrawer();
      } catch (error) {
        if (message) message.textContent = error.message;
      } finally {
        bundleButton.disabled = false;
      }
      return;
    }

    const thumb = event.target.closest('[data-product-thumb]');
    if (thumb) {
      const gallery = thumb.closest('[data-product-gallery]');
      const mainImage = gallery?.querySelector('[data-product-main-image]');
      gallery?.querySelectorAll('[data-product-thumb]').forEach(item => item.classList.remove('is-active'));
      thumb.classList.add('is-active');
      if (mainImage) {
        mainImage.src = thumb.dataset.imageUrl;
        mainImage.srcset = '';
        mainImage.alt = thumb.dataset.imageAlt || '';
      }
    }
  });

  document.addEventListener('submit', async event => {
    const form = event.target.closest('form[data-ajax-product-form], form.product-form');
    if (!form) return;
    event.preventDefault();
    const submit = form.querySelector('[type="submit"]');
    const message = form.querySelector('[data-form-message]');
    if (submit) submit.disabled = true;
    if (message) message.textContent = '';
    try {
      const formData = new FormData(form);
      await addItems([{ id: Number(formData.get('id')), quantity: Math.max(1, Number(formData.get('quantity') || 1)) }]);
      await openDrawer();
    } catch (error) {
      if (message) message.textContent = error.message;
    } finally {
      if (submit) submit.disabled = false;
    }
  });

  document.addEventListener('change', async event => {
    const optionSelector = event.target.closest('[data-option-selector]');
    if (optionSelector) {
      updateProductVariant(optionSelector);
      return;
    }

    const drawerQuantity = event.target.closest('[data-cart-quantity]');
    if (drawerQuantity) {
      try { await changeLine(drawerQuantity.dataset.lineKey, drawerQuantity.value); } catch (error) { console.error(error); }
      return;
    }

    const pageQuantity = event.target.closest('[data-cart-page-quantity]');
    if (pageQuantity) {
      try {
        await changeLine(pageQuantity.dataset.lineKey, pageQuantity.value);
        window.location.reload();
      } catch (error) { console.error(error); }
      return;
    }

    if (event.target.closest('[data-bundle-variant], [data-bundle-quantity]')) {
      updateBundleSection(event.target.closest('[data-bundle-section]'));
    }
  });

  document.addEventListener('input', event => {
    const productQuantity = event.target.closest('[data-product-quantity]');
    if (productQuantity) updateProductTotal(productQuantity.closest('[data-product-root]'));

    const bundleQuantity = event.target.closest('[data-bundle-quantity]');
    if (bundleQuantity) updateBundleSection(bundleQuantity.closest('[data-bundle-section]'));
  });

  document.addEventListener('keydown', event => {
    if (event.key === 'Escape') closeDrawer();
  });

  document.addEventListener('DOMContentLoaded', async () => {
    activateCodes(window.CISQO.welcomeCode);
    document.querySelectorAll('[data-product-root]').forEach(updateProductTotal);
    document.querySelectorAll('[data-bundle-section]').forEach(updateBundleSection);
    await refreshCart();
  });
})();
